/**
 * BrandCenterV2 — Centre de marque Esprit Padel OS.
 *
 * Centralise la charte graphique, les règles de la marque et les accès aux
 * applications utilisées par l'équipe comm.
 *
 * Source de données : une clé appdata unique `ep:brand` (objet, format V1
 * { value: {...}, updatedAt }). Lecture live via onSnapshot, écriture via
 * writeAppdata (setDoc) — même pattern que CalendrierV2 / RessourcesV2.
 *
 * Droits :
 *   - isAdmin = currentUser?.admin === true → ajoute / modifie / retire tout.
 *   - Charte, règles et applications : visibles par tous les connectés.
 *   - Documents « clubs » : visibles si publiés & non archivés ET (aucun club
 *     ciblé = tous, OU intersection avec currentUser.clubs). L'admin voit tout.
 *
 * Aucun upload Storage : logos / charte / documents sont des liens externes
 * (Drive, Canva, URL directe), cohérent avec le reste de la V2.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { writeAppdata } from "../hooks/useCalendarWrite";
import { useClubs } from "../hooks/useV1Data";
import { isAdmin as isAdminUser } from "../lib/roles";
import {
  IconPalette, IconExternalLink, IconPlus, IconX, IconSettings,
  IconChevronRight, IconFolder, IconMetricool,
} from "../icons";

/* ─────────────────────────────────────
   HELPERS
───────────────────────────────────── */
function uid() {
  return String(Date.now()) + String(Math.random()).slice(2, 8);
}

const EMPTY_BRAND = {
  colors: [],       // { id, name, hex, usage }
  fonts: [],        // { id, name, usage, url }
  logos: [],        // { id, name, previewUrl, downloadUrl, bg }  bg: "light" | "dark"
  logoRules: "",
  charteUrl: "",
  rules: { tone: "", naming: "", photoVideo: "", social: "", dosDonts: [] }, // dosDonts: { id, kind, text }
  apps: [],         // { id, name, logoUrl, description, url, guideUrl, contact }
  docs: [],         // { id, name, type, url, description, clubs:[], published, archived }
};

/* Lecture live de la clé ep:brand, fusionnée avec les valeurs par défaut. */
function useBrand() {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!db) { setLoading(false); return undefined; }
    const unsub = onSnapshot(
      doc(db, "appdata", "ep:brand"),
      (snap) => {
        const v = snap.exists() ? snap.data().value : undefined;
        setRaw(v && typeof v === "object" && !Array.isArray(v) ? v : null);
        setLoading(false);
      },
      () => { setRaw(null); setLoading(false); }
    );
    return () => unsub && unsub();
  }, []);
  const brand = useMemo(() => ({
    ...EMPTY_BRAND,
    ...(raw || {}),
    rules: { ...EMPTY_BRAND.rules, ...((raw && raw.rules) || {}) },
  }), [raw]);
  return { brand, loading };
}

/* Luminance → texte sombre ou clair lisible sur une pastille de couleur. */
function readableText(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return "#18181a";
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#18181a" : "#ffffff";
}

function normHex(v) {
  let s = (v || "").trim();
  if (s && !s.startsWith("#")) s = "#" + s;
  return s;
}

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
}

/* Rendu d'un bloc texte multi-ligne (règles) en préservant les sauts de ligne. */
function MultiLine({ text }) {
  if (!text) return null;
  return (
    <>
      {String(text).split("\n").map((line, i) => (
        <p key={i} className="brand-text-line">{line || " "}</p>
      ))}
    </>
  );
}

/* ─────────────────────────────────────
   ICÔNES INLINE
───────────────────────────────────── */
function IconCopy({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}
function IconDownload({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7,10 12,15 17,10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function IconTrash({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,6 5,6 21,6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}
function IconCheck({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );
}

/* ─────────────────────────────────────
   TOAST (copie)
───────────────────────────────────── */
function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg) => {
    setToast(msg);
    window.clearTimeout(useToast._t);
    useToast._t = window.setTimeout(() => setToast(null), 1800);
  }, []);
  return [toast, show];
}

/* ─────────────────────────────────────
   ONGLET 1 — CHARTE GRAPHIQUE
───────────────────────────────────── */
function CharteTab({ brand, isAdmin, onEdit, onCopy }) {
  const { colors, fonts, logos, logoRules, charteUrl } = brand;
  const empty = !colors.length && !fonts.length && !logos.length && !logoRules && !charteUrl;

  return (
    <div className="brand-sections">
      {/* Logos */}
      <section className="brand-block">
        <div className="brand-block__head">
          <h2 className="brand-block__title">Logos officiels</h2>
          {isAdmin && (
            <button className="camp-btn camp-btn--ghost camp-btn--sm" onClick={() => onEdit("charte")}>
              <IconSettings size={13} /> Modifier
            </button>
          )}
        </div>
        {logos.length === 0 ? (
          <p className="brand-empty-line">Aucun logo référencé.</p>
        ) : (
          <div className="brand-logo-grid">
            {logos.map((l) => (
              <div key={l.id} className={`brand-logo-card brand-logo-card--${l.bg === "dark" ? "dark" : "light"}`}>
                <div className="brand-logo-card__preview">
                  {l.previewUrl
                    ? <img src={l.previewUrl} alt={l.name || "Logo"} loading="lazy" />
                    : <IconPalette size={28} />}
                </div>
                <div className="brand-logo-card__foot">
                  <span className="brand-logo-card__name">{l.name || "Logo"}</span>
                  {l.downloadUrl && (
                    <a className="brand-logo-card__dl" href={l.downloadUrl} download target="_blank" rel="noopener noreferrer">
                      <IconDownload size={13} /> Télécharger
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Couleurs */}
      <section className="brand-block">
        <div className="brand-block__head">
          <h2 className="brand-block__title">Couleurs de la marque</h2>
        </div>
        {colors.length === 0 ? (
          <p className="brand-empty-line">Aucune couleur définie.</p>
        ) : (
          <div className="brand-color-grid">
            {colors.map((c) => (
              <button key={c.id} className="brand-color"
                style={{ "--sw": c.hex, color: readableText(c.hex) }}
                onClick={() => onCopy(c.hex)} title="Copier le code">
                <span className="brand-color__hex">{c.hex}</span>
                <span className="brand-color__copy"><IconCopy size={13} /></span>
                <span className="brand-color__foot" style={{ color: "var(--text)" }}>
                  <span className="brand-color__name">{c.name || "Couleur"}</span>
                  {c.usage && <span className="brand-color__usage">{c.usage}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Typographies */}
      <section className="brand-block">
        <div className="brand-block__head">
          <h2 className="brand-block__title">Typographies</h2>
        </div>
        {fonts.length === 0 ? (
          <p className="brand-empty-line">Aucune typographie définie.</p>
        ) : (
          <div className="brand-font-list">
            {fonts.map((f) => (
              <div key={f.id} className="brand-font">
                <div className="brand-font__sample">Aa</div>
                <div className="brand-font__body">
                  <span className="brand-font__name">{f.name || "Police"}</span>
                  {f.usage && <span className="brand-font__usage">{f.usage}</span>}
                </div>
                {f.url && (
                  <a className="brand-font__link" href={f.url} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink size={13} /> Obtenir
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Consignes logos */}
      {(logoRules || isAdmin) && (
        <section className="brand-block">
          <div className="brand-block__head">
            <h2 className="brand-block__title">Consignes d'utilisation des logos</h2>
          </div>
          {logoRules
            ? <div className="brand-note"><MultiLine text={logoRules} /></div>
            : <p className="brand-empty-line">Dimensions, marges, fonds autorisés, usages à éviter… à renseigner.</p>}
        </section>
      )}

      {/* Charte complète */}
      <section className="brand-block">
        <div className="brand-block__head">
          <h2 className="brand-block__title">Charte graphique complète</h2>
        </div>
        {charteUrl ? (
          <a className="brand-charte-cta" href={charteUrl} target="_blank" rel="noopener noreferrer">
            <span className="brand-charte-cta__icon"><IconPalette size={22} /></span>
            <span className="brand-charte-cta__body">
              <span className="brand-charte-cta__title">Consulter / télécharger la charte</span>
              <span className="brand-charte-cta__sub">Document complet de référence</span>
            </span>
            <IconExternalLink size={16} />
          </a>
        ) : (
          <p className="brand-empty-line">Lien vers la charte complète à renseigner.</p>
        )}
      </section>

      {empty && !isAdmin && (
        <div className="brand-global-empty">
          <IconPalette size={34} />
          <p>La charte graphique n'a pas encore été renseignée.</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   ONGLET 2 — RÈGLES DE LA MARQUE
───────────────────────────────────── */
const RULE_BLOCKS = [
  { key: "tone",       title: "Ton & style rédactionnel" },
  { key: "naming",     title: "Écriture du nom de la marque et des clubs" },
  { key: "photoVideo", title: "Recommandations photos & vidéos" },
  { key: "social",     title: "Communication sur les réseaux sociaux" },
];

function ReglesTab({ brand, isAdmin, onEdit }) {
  const { rules } = brand;
  const dos = (rules.dosDonts || []).filter((d) => d.kind === "do");
  const donts = (rules.dosDonts || []).filter((d) => d.kind === "dont");
  const anyText = RULE_BLOCKS.some((b) => rules[b.key]) || (rules.dosDonts || []).length;

  return (
    <div className="brand-sections">
      <div className="brand-block__head brand-block__head--page">
        <h2 className="brand-block__title">Règles communes de communication</h2>
        {isAdmin && (
          <button className="camp-btn camp-btn--ghost camp-btn--sm" onClick={() => onEdit("regles")}>
            <IconSettings size={13} /> Modifier
          </button>
        )}
      </div>

      {RULE_BLOCKS.map((b) => (
        (rules[b.key] || isAdmin) && (
          <section key={b.key} className="brand-block">
            <h3 className="brand-block__subtitle">{b.title}</h3>
            {rules[b.key]
              ? <div className="brand-note"><MultiLine text={rules[b.key]} /></div>
              : <p className="brand-empty-line">À renseigner.</p>}
          </section>
        )
      ))}

      {(dos.length > 0 || donts.length > 0 || isAdmin) && (
        <section className="brand-block">
          <h3 className="brand-block__subtitle">Bonnes pratiques & usages à éviter</h3>
          <div className="brand-dd-grid">
            <div className="brand-dd brand-dd--do">
              <div className="brand-dd__head"><IconCheck size={14} /> À faire</div>
              {dos.length === 0 ? <p className="brand-empty-line">—</p> : (
                <ul className="brand-dd__list">
                  {dos.map((d) => <li key={d.id}>{d.text}</li>)}
                </ul>
              )}
            </div>
            <div className="brand-dd brand-dd--dont">
              <div className="brand-dd__head"><IconX size={13} /> À éviter</div>
              {donts.length === 0 ? <p className="brand-empty-line">—</p> : (
                <ul className="brand-dd__list">
                  {donts.map((d) => <li key={d.id}>{d.text}</li>)}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {!anyText && !isAdmin && (
        <div className="brand-global-empty">
          <IconFolder size={34} />
          <p>Les règles de la marque n'ont pas encore été renseignées.</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   ONGLET 3 — APPLICATIONS
───────────────────────────────────── */
function AppsTab({ brand, isAdmin, onEdit }) {
  const { apps } = brand;
  return (
    <div className="brand-sections">
      <div className="brand-block__head brand-block__head--page">
        <h2 className="brand-block__title">Applications utilisées</h2>
        {isAdmin && (
          <button className="camp-btn camp-btn--ghost camp-btn--sm" onClick={() => onEdit("apps")}>
            <IconSettings size={13} /> Modifier
          </button>
        )}
      </div>

      {apps.length === 0 ? (
        <div className="brand-global-empty">
          <IconMetricool size={32} />
          <p>Aucune application référencée pour l'instant.{isAdmin && " Ajoutez vos outils (Metricool, Canva, stockage…)."}</p>
        </div>
      ) : (
        <div className="brand-app-grid">
          {apps.map((a) => (
            <div key={a.id} className="brand-app-card">
              <div className="brand-app-card__top">
                <div className="brand-app-card__logo">
                  {a.logoUrl
                    ? <img src={a.logoUrl} alt={a.name || "App"} loading="lazy" />
                    : <IconExternalLink size={18} />}
                </div>
                <span className="brand-app-card__name">{a.name || "Application"}</span>
              </div>
              {a.description && <p className="brand-app-card__desc">{a.description}</p>}
              <div className="brand-app-card__actions">
                {a.url && (
                  <a className="camp-btn camp-btn--primary camp-btn--sm" href={a.url} target="_blank" rel="noopener noreferrer">
                    Ouvrir <IconExternalLink size={12} />
                  </a>
                )}
                {a.guideUrl && (
                  <a className="camp-btn camp-btn--ghost camp-btn--sm" href={a.guideUrl} target="_blank" rel="noopener noreferrer">
                    Guide
                  </a>
                )}
              </div>
              {a.contact && (
                isEmail(a.contact)
                  ? <a className="brand-app-card__contact" href={`mailto:${a.contact}`}>Demander un accès</a>
                  : <span className="brand-app-card__contact brand-app-card__contact--txt">Accès : {a.contact}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   ONGLET 4 — DOCUMENTS CLUBS
───────────────────────────────────── */
const DOC_TYPES = [
  { value: "doc",   label: "Document" },
  { value: "link",  label: "Lien" },
  { value: "canva", label: "Canva" },
  { value: "image", label: "Image" },
  { value: "video", label: "Vidéo" },
];
function docTypeLabel(t) { return (DOC_TYPES.find((x) => x.value === t) || {}).label || "Document"; }

function DocsTab({ brand, clubs, isAdmin, onAdd, onEditDoc, onToggleArchive }) {
  const [filterClub, setFilterClub] = useState("");
  const list = useMemo(() => {
    let l = brand.docs || [];
    if (filterClub) l = l.filter((d) => (d.clubs || []).map(String).includes(String(filterClub)));
    return [...l].sort((a, b) => (a.archived === b.archived ? 0 : a.archived ? 1 : -1));
  }, [brand.docs, filterClub]);

  function clubNames(ids) {
    if (!ids || ids.length === 0) return [{ id: "_all", name: "Tous les clubs", color: "#9896a0" }];
    return ids.map((id) => {
      const c = clubs.find((x) => String(x.id) === String(id));
      return { id, name: c?.name || id, color: c?.color || "#9896a0" };
    });
  }

  return (
    <div className="brand-sections">
      <div className="brand-block__head brand-block__head--page">
        <div>
          <h2 className="brand-block__title">Documents & liens par club</h2>
          <p className="brand-block__hint">Ressources communes à tous, ou réservées à certains clubs.</p>
        </div>
        {isAdmin && (
          <button className="camp-btn camp-btn--primary camp-btn--sm" onClick={onAdd}>
            <IconPlus size={13} /> Ajouter
          </button>
        )}
      </div>

      {clubs.length > 0 && (brand.docs || []).length > 0 && (
        <div className="brand-doc-filter">
          <button className={`brand-chip${!filterClub ? " active" : ""}`} onClick={() => setFilterClub("")}>Tous</button>
          {clubs.filter((c) => c.name).map((c) => (
            <button key={c.id}
              className={`brand-chip${String(filterClub) === String(c.id) ? " active" : ""}`}
              style={String(filterClub) === String(c.id) ? { borderColor: c.color, color: c.color, background: (c.color || "#000") + "14" } : undefined}
              onClick={() => setFilterClub(c.id)}>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {list.length === 0 ? (
        <div className="brand-global-empty">
          <IconFolder size={32} />
          <p>Aucun document pour l'instant.{isAdmin && " Ajoutez un premier document commun ou propre à un club."}</p>
        </div>
      ) : (
        <div className="brand-doc-list">
          {list.map((d) => (
            <div key={d.id} className={`brand-doc${d.archived ? " brand-doc--archived" : ""}`}>
              <div className="brand-doc__main">
                <div className="brand-doc__head">
                  <span className="brand-doc__name">{d.name || "Document"}</span>
                  <span className="v2-badge v2-badge--gray brand-doc__type">{docTypeLabel(d.type)}</span>
                  {isAdmin && !d.published && <span className="v2-badge v2-badge--yellow">Brouillon</span>}
                  {isAdmin && d.archived && <span className="v2-badge v2-badge--gray">Archivé</span>}
                </div>
                {d.description && <p className="brand-doc__desc">{d.description}</p>}
                <div className="brand-doc__clubs">
                  {clubNames(d.clubs).map((c) => (
                    <span key={c.id} className="brand-doc__club" style={{ color: c.color, borderColor: c.color }}>{c.name}</span>
                  ))}
                </div>
              </div>
              <div className="brand-doc__actions">
                {d.url && (
                  <a className="camp-btn camp-btn--outline camp-btn--sm" href={d.url} target="_blank" rel="noopener noreferrer">
                    Ouvrir <IconExternalLink size={12} />
                  </a>
                )}
                {isAdmin && (
                  <>
                    <button className="camp-btn camp-btn--ghost camp-btn--sm" onClick={() => onEditDoc(d)}>
                      <IconSettings size={13} />
                    </button>
                    <button className="camp-btn camp-btn--ghost camp-btn--sm" onClick={() => onToggleArchive(d)}>
                      {d.archived ? "Désarchiver" : "Archiver"}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   PANNEAU GÉNÉRIQUE
───────────────────────────────────── */
function Panel({ title, onClose, children, onSubmit, saving, err, submitLabel = "Enregistrer" }) {
  return (
    <>
      <div className="camp-panel-overlay" onClick={onClose} />
      <div className="camp-panel camp-panel--large">
        <div className="camp-panel__header">
          <span className="camp-panel__title">{title}</span>
          <button className="camp-panel__close" onClick={onClose} aria-label="Fermer"><IconX size={16} /></button>
        </div>
        <form className="camp-panel__body" onSubmit={onSubmit}>
          {children}
          {err && <div className="camp-form-error camp-form-error--block">{err}</div>}
          <div className="camp-panel__footer">
            <button type="button" className="camp-btn camp-btn--ghost" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="camp-btn camp-btn--primary" disabled={saving}>
              {saving ? <span className="v2-spinner camp-spinner" /> : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* Ligne répétable générique : rend `fields(row, set)` + bouton suppression. */
function Repeater({ label, hint, rows, onAdd, onRemove, addLabel, children }) {
  return (
    <div className="camp-form-group">
      <label className="camp-form-label">{label}</label>
      {hint && <span className="camp-form-hint">{hint}</span>}
      <div className="brand-repeater">
        {rows.length === 0 && <p className="brand-empty-line">Aucun élément.</p>}
        {rows.map((row, i) => (
          <div key={row.id} className="brand-repeater__row">
            <div className="brand-repeater__fields">{children(row, i)}</div>
            <button type="button" className="brand-repeater__del" onClick={() => onRemove(row.id)} aria-label="Supprimer">
              <IconTrash size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="camp-btn camp-btn--ghost camp-btn--sm brand-repeater__add" onClick={onAdd}>
        <IconPlus size={13} /> {addLabel}
      </button>
    </div>
  );
}

/* ── Panneau Charte ── */
function ChartePanel({ brand, onClose, onSave }) {
  const [colors, setColors] = useState(() => brand.colors.map((c) => ({ ...c })));
  const [fonts, setFonts] = useState(() => brand.fonts.map((f) => ({ ...f })));
  const [logos, setLogos] = useState(() => brand.logos.map((l) => ({ ...l })));
  const [logoRules, setLogoRules] = useState(brand.logoRules || "");
  const [charteUrl, setCharteUrl] = useState(brand.charteUrl || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const upd = (setter) => (id, key, val) => setter((rows) => rows.map((r) => (r.id === id ? { ...r, [key]: val } : r)));
  const rm = (setter) => (id) => setter((rows) => rows.filter((r) => r.id !== id));
  const updColor = upd(setColors), updFont = upd(setFonts), updLogo = upd(setLogos);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await onSave({
        colors: colors.map((c) => ({ ...c, hex: normHex(c.hex) })).filter((c) => c.name || c.hex),
        fonts: fonts.filter((f) => f.name || f.url),
        logos: logos.filter((l) => l.name || l.previewUrl || l.downloadUrl),
        logoRules: logoRules.trim(),
        charteUrl: charteUrl.trim(),
      });
      onClose();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <Panel title="Modifier la charte graphique" onClose={onClose} onSubmit={submit} saving={saving} err={err}>
      <Repeater label="Logos" hint="Liens d'aperçu et de téléchargement (Drive, Canva, URL directe)."
        rows={logos} addLabel="Ajouter un logo"
        onAdd={() => setLogos((r) => [...r, { id: uid(), name: "", previewUrl: "", downloadUrl: "", bg: "light" }])}
        onRemove={rm(setLogos)}>
        {(row) => (
          <>
            <input className="camp-form-input" placeholder="Nom (ex : Logo principal)" value={row.name} onChange={(e) => updLogo(row.id, "name", e.target.value)} />
            <input className="camp-form-input" placeholder="URL d'aperçu (image)" value={row.previewUrl} onChange={(e) => updLogo(row.id, "previewUrl", e.target.value)} />
            <input className="camp-form-input" placeholder="URL de téléchargement" value={row.downloadUrl} onChange={(e) => updLogo(row.id, "downloadUrl", e.target.value)} />
            <select className="camp-form-input" value={row.bg || "light"} onChange={(e) => updLogo(row.id, "bg", e.target.value)}>
              <option value="light">Fond clair</option>
              <option value="dark">Fond foncé</option>
            </select>
          </>
        )}
      </Repeater>

      <Repeater label="Couleurs" rows={colors} addLabel="Ajouter une couleur"
        onAdd={() => setColors((r) => [...r, { id: uid(), name: "", hex: "#", usage: "" }])}
        onRemove={rm(setColors)}>
        {(row) => (
          <>
            <input className="camp-form-input" placeholder="Nom (ex : Jaune Esprit)" value={row.name} onChange={(e) => updColor(row.id, "name", e.target.value)} />
            <input className="camp-form-input" placeholder="#FEB601" value={row.hex} onChange={(e) => updColor(row.id, "hex", e.target.value)} />
            <input className="camp-form-input" placeholder="Usage (ex : CTA, fonds)" value={row.usage} onChange={(e) => updColor(row.id, "usage", e.target.value)} />
          </>
        )}
      </Repeater>

      <Repeater label="Typographies" rows={fonts} addLabel="Ajouter une police"
        onAdd={() => setFonts((r) => [...r, { id: uid(), name: "", usage: "", url: "" }])}
        onRemove={rm(setFonts)}>
        {(row) => (
          <>
            <input className="camp-form-input" placeholder="Nom (ex : Inter)" value={row.name} onChange={(e) => updFont(row.id, "name", e.target.value)} />
            <input className="camp-form-input" placeholder="Usage (ex : Titres)" value={row.usage} onChange={(e) => updFont(row.id, "usage", e.target.value)} />
            <input className="camp-form-input" placeholder="Lien (Google Fonts…)" value={row.url} onChange={(e) => updFont(row.id, "url", e.target.value)} />
          </>
        )}
      </Repeater>

      <div className="camp-form-group">
        <label className="camp-form-label">Consignes d'utilisation des logos</label>
        <textarea className="camp-form-input camp-form-textarea" rows={4} value={logoRules}
          onChange={(e) => setLogoRules(e.target.value)}
          placeholder="Dimensions minimales, marges de sécurité, fonds autorisés, usages à éviter…" />
      </div>
      <div className="camp-form-group">
        <label className="camp-form-label">Lien vers la charte complète</label>
        <input className="camp-form-input" type="url" placeholder="https://" value={charteUrl} onChange={(e) => setCharteUrl(e.target.value)} />
      </div>
    </Panel>
  );
}

/* ── Panneau Règles ── */
function ReglesPanel({ brand, onClose, onSave }) {
  const [vals, setVals] = useState(() => ({
    tone: brand.rules.tone || "", naming: brand.rules.naming || "",
    photoVideo: brand.rules.photoVideo || "", social: brand.rules.social || "",
  }));
  const [dosDonts, setDosDonts] = useState(() => (brand.rules.dosDonts || []).map((d) => ({ ...d })));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const updDD = (id, key, val) => setDosDonts((r) => r.map((x) => (x.id === id ? { ...x, [key]: val } : x)));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await onSave({
        rules: {
          tone: vals.tone.trim(), naming: vals.naming.trim(),
          photoVideo: vals.photoVideo.trim(), social: vals.social.trim(),
          dosDonts: dosDonts.filter((d) => (d.text || "").trim()).map((d) => ({ ...d, text: d.text.trim() })),
        },
      });
      onClose();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <Panel title="Modifier les règles de la marque" onClose={onClose} onSubmit={submit} saving={saving} err={err}>
      {RULE_BLOCKS.map((b) => (
        <div key={b.key} className="camp-form-group">
          <label className="camp-form-label">{b.title}</label>
          <textarea className="camp-form-input camp-form-textarea" rows={3} value={vals[b.key]}
            onChange={(e) => setVals((v) => ({ ...v, [b.key]: e.target.value }))} />
        </div>
      ))}
      <Repeater label="Bonnes pratiques & usages à éviter" rows={dosDonts} addLabel="Ajouter une ligne"
        onAdd={() => setDosDonts((r) => [...r, { id: uid(), kind: "do", text: "" }])}
        onRemove={(id) => setDosDonts((r) => r.filter((x) => x.id !== id))}>
        {(row) => (
          <>
            <select className="camp-form-input" value={row.kind} onChange={(e) => updDD(row.id, "kind", e.target.value)}>
              <option value="do">À faire</option>
              <option value="dont">À éviter</option>
            </select>
            <input className="camp-form-input" placeholder="Texte" value={row.text} onChange={(e) => updDD(row.id, "text", e.target.value)} />
          </>
        )}
      </Repeater>
    </Panel>
  );
}

/* ── Panneau Applications ── */
function AppsPanel({ brand, onClose, onSave }) {
  const [apps, setApps] = useState(() => brand.apps.map((a) => ({ ...a })));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const upd = (id, key, val) => setApps((r) => r.map((a) => (a.id === id ? { ...a, [key]: val } : a)));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await onSave({ apps: apps.filter((a) => (a.name || "").trim() || (a.url || "").trim()) });
      onClose();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <Panel title="Modifier les applications" onClose={onClose} onSubmit={submit} saving={saving} err={err}>
      <Repeater label="Applications" hint="Nom, logo, description, lien d'ouverture, guide et contact d'accès."
        rows={apps} addLabel="Ajouter une application"
        onAdd={() => setApps((r) => [...r, { id: uid(), name: "", logoUrl: "", description: "", url: "", guideUrl: "", contact: "" }])}
        onRemove={(id) => setApps((r) => r.filter((a) => a.id !== id))}>
        {(row) => (
          <>
            <input className="camp-form-input" placeholder="Nom (ex : Metricool)" value={row.name} onChange={(e) => upd(row.id, "name", e.target.value)} />
            <input className="camp-form-input" placeholder="URL du logo" value={row.logoUrl} onChange={(e) => upd(row.id, "logoUrl", e.target.value)} />
            <input className="camp-form-input" placeholder="Description courte" value={row.description} onChange={(e) => upd(row.id, "description", e.target.value)} />
            <input className="camp-form-input" type="url" placeholder="Lien à ouvrir (https://)" value={row.url} onChange={(e) => upd(row.id, "url", e.target.value)} />
            <input className="camp-form-input" type="url" placeholder="Lien guide (optionnel)" value={row.guideUrl} onChange={(e) => upd(row.id, "guideUrl", e.target.value)} />
            <input className="camp-form-input" placeholder="Contact accès (email ou nom)" value={row.contact} onChange={(e) => upd(row.id, "contact", e.target.value)} />
          </>
        )}
      </Repeater>
    </Panel>
  );
}

/* ── Panneau Document (création / édition) ── */
function DocPanel({ doc: editing, clubs, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    id: editing?.id || null,
    name: editing?.name || "",
    type: editing?.type || "doc",
    url: editing?.url || "",
    description: editing?.description || "",
    clubs: editing?.clubs ? [...editing.clubs] : [],
    published: editing?.published ?? true,
    archived: editing?.archived || false,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  function toggleClub(id) {
    setForm((f) => ({ ...f, clubs: f.clubs.map(String).includes(String(id)) ? f.clubs.filter((x) => String(x) !== String(id)) : [...f.clubs, id] }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Le nom est obligatoire."); return; }
    if (!form.url.trim()) { setErr("Le lien est obligatoire."); return; }
    setSaving(true); setErr(null);
    try {
      await onSave({ ...form, name: form.name.trim(), url: form.url.trim(), description: form.description.trim() });
      onClose();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <Panel title={editing ? "Modifier le document" : "Nouveau document"} onClose={onClose} onSubmit={submit} saving={saving} err={err}
      submitLabel={editing ? "Enregistrer" : "Créer"}>
      <div className="camp-form-group">
        <label className="camp-form-label">Nom <span style={{ color: "#DC2626" }}>*</span></label>
        <input className="camp-form-input" value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus maxLength={120} />
      </div>
      <div className="camp-form-row">
        <div className="camp-form-group">
          <label className="camp-form-label">Type</label>
          <select className="camp-form-input" value={form.type} onChange={(e) => set("type", e.target.value)}>
            {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="camp-form-group">
          <label className="camp-form-label">Lien <span style={{ color: "#DC2626" }}>*</span></label>
          <input className="camp-form-input" type="url" placeholder="https://" value={form.url} onChange={(e) => set("url", e.target.value)} />
        </div>
      </div>
      <div className="camp-form-group">
        <label className="camp-form-label">Description</label>
        <textarea className="camp-form-input camp-form-textarea" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
      </div>
      <div className="camp-form-group">
        <label className="camp-form-label">Clubs <span className="camp-form-hint">(aucun sélectionné = visible par tous les clubs)</span></label>
        <div className="rsrc-multi-select">
          {clubs.filter((c) => c.name).map((c) => (
            <button key={c.id} type="button"
              className={`rsrc-multi-btn${form.clubs.map(String).includes(String(c.id)) ? " active" : ""}`}
              onClick={() => toggleClub(c.id)}>{c.name}</button>
          ))}
        </div>
      </div>
      <div className="camp-form-group">
        <label className="brand-toggle">
          <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} />
          Publié (visible par les clubs concernés)
        </label>
      </div>
    </Panel>
  );
}

/* ─────────────────────────────────────
   COMPOSANT PRINCIPAL
───────────────────────────────────── */
const TABS = [
  { id: "charte", label: "Charte graphique" },
  { id: "regles", label: "Règles de la marque" },
  { id: "apps",   label: "Applications" },
  { id: "docs",   label: "Documents clubs" },
];

export default function BrandCenterV2({ currentUser, appId }) {
  // Droits d'édition : rôle canonique (roles.js). admin = Responsable comm (Mélissa).
  const isAdmin = isAdminUser(currentUser);
  const { brand, loading } = useBrand();
  const { clubs } = useClubs();
  const [tab, setTab] = useState("charte");
  const [panel, setPanel] = useState(null);      // null | "charte" | "regles" | "apps"
  const [docPanel, setDocPanel] = useState(null); // null | { doc } (doc null = création)
  const [toast, showToast] = useToast();

  const userClubs = useMemo(() => (currentUser?.clubs || []).map(String), [currentUser]);

  // Documents visibles selon le rôle + le périmètre club.
  const visibleDocs = useMemo(() => {
    const all = brand.docs || [];
    if (isAdmin) return all;
    return all.filter((d) => {
      if (!d.published || d.archived) return false;
      const targets = (d.clubs || []).map(String);
      return targets.length === 0 || targets.some((t) => userClubs.includes(t));
    });
  }, [brand.docs, isAdmin, userClubs]);

  const copyHex = useCallback((hex) => {
    const val = normHex(hex);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(val).then(() => showToast(`${val} copié`)).catch(() => showToast(val));
    } else {
      showToast(val);
    }
  }, [showToast]);

  // Écrit un patch partiel sur l'objet ep:brand (merge complet préservé).
  const savePatch = useCallback(async (patch) => {
    const next = { ...EMPTY_BRAND, ...brand, ...patch };
    // garde rules complet si patché
    if (patch.rules) next.rules = { ...EMPTY_BRAND.rules, ...patch.rules };
    await writeAppdata("ep:brand", next);
  }, [brand]);

  const saveDoc = useCallback(async (form) => {
    const docs = brand.docs || [];
    let next;
    if (form.id && docs.some((d) => d.id === form.id)) {
      next = docs.map((d) => (d.id === form.id ? { ...d, ...form, updatedAt: new Date().toISOString() } : d));
    } else {
      next = [...docs, { ...form, id: uid(), createdBy: appId, createdAt: new Date().toISOString() }];
    }
    await savePatch({ docs: next });
  }, [brand.docs, appId, savePatch]);

  const toggleArchiveDoc = useCallback(async (d) => {
    const next = (brand.docs || []).map((x) => (x.id === d.id ? { ...x, archived: !x.archived } : x));
    await savePatch({ docs: next });
  }, [brand.docs, savePatch]);

  const brandForDocs = useMemo(() => ({ ...brand, docs: visibleDocs }), [brand, visibleDocs]);

  return (
    <div className="rsrc-page brand-page">
      <div className="rsrc-main">
        <div className="rsrc-header">
          <div className="rsrc-header__left">
            <h1 className="rsrc-header__title">Brand Center</h1>
            <p className="rsrc-header__sub">Charte graphique, règles de la marque et accès aux applications</p>
          </div>
        </div>

        {/* Onglets */}
        <div className="brand-tabs">
          {TABS.map((t) => (
            <button key={t.id} className={`brand-tab${tab === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === "docs" && visibleDocs.length > 0 && <span className="brand-tab__count">{visibleDocs.length}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="rsrc-loading"><div className="v2-spinner" /></div>
        ) : (
          <>
            {tab === "charte" && <CharteTab brand={brand} isAdmin={isAdmin} onEdit={setPanel} onCopy={copyHex} />}
            {tab === "regles" && <ReglesTab brand={brand} isAdmin={isAdmin} onEdit={setPanel} />}
            {tab === "apps"   && <AppsTab brand={brand} isAdmin={isAdmin} onEdit={setPanel} />}
            {tab === "docs"   && (
              <DocsTab brand={brandForDocs} clubs={clubs} isAdmin={isAdmin}
                onAdd={() => setDocPanel({ doc: null })}
                onEditDoc={(d) => setDocPanel({ doc: d })}
                onToggleArchive={toggleArchiveDoc} />
            )}
          </>
        )}
      </div>

      {/* Panneaux d'édition (admin) */}
      {isAdmin && panel === "charte" && <ChartePanel brand={brand} onClose={() => setPanel(null)} onSave={savePatch} />}
      {isAdmin && panel === "regles" && <ReglesPanel brand={brand} onClose={() => setPanel(null)} onSave={savePatch} />}
      {isAdmin && panel === "apps"   && <AppsPanel brand={brand} onClose={() => setPanel(null)} onSave={savePatch} />}
      {isAdmin && docPanel && (
        <DocPanel doc={docPanel.doc} clubs={clubs} onClose={() => setDocPanel(null)} onSave={saveDoc} />
      )}

      {toast && <div className="brand-toast">{toast}</div>}
    </div>
  );
}
